import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import type { FilterSelection } from '@/domain/prices/filters/types';
import { createPricesFiltersParser } from '@/infrastructure/prices/pricesFiltersParser';
import type {
  TransportSelectorContext,
  TransportSelectorExtraFilters,
  TransportSelectorFilters,
} from '@/infrastructure/prices/transport/api/types';

export enum TransportType {
  Flight = 'Flight',
  SelfDrive = 'SelfDrive',
  Bus = 'Bus',
}

const apiEndpoints: Record<TransportType, string> = {
  Flight: 'FlightSelector/GetFlightSelectorApi',
  SelfDrive: 'SelfdriveSelector/GetSelfdriveSelectorApi',
  Bus: 'BusSelector/GetBusSelectorApi',
};

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  queryStringSerializer: QueryStringSerializer<
    TransportSelectorContext & FilterSelection & TransportSelectorExtraFilters
  >;
}

export interface TransportSelectorUrlBuilder {
  buildUrl: (
    criteria: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters,
    transportType: TransportType
  ) => string;
}

export const createTransportSelectorUrlBuilder = (dependencies: Partial<Dependencies> = {}) => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    queryStringSerializer = createQueryStringSerializer({
      parser: createPricesFiltersParser(),
    }),
  } = dependencies as Dependencies;

  function buildUrl(
    {
      accoId,
      contextItemId,
      bookingGateId,
      filterSelection,
      selectedPriceId,
      roomIds,
      roomContractIds,
    }: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters,
    transportType: TransportType
  ) {
    const roomSelectorContext = {
      accoId,
      bookingGateId,
      contextItemId,
    };

    const duration = selectedPriceId?.split('|')[0];
    const departureDate = selectedPriceId?.split('|')[1];

    const serializedCriteria = queryStringSerializer.serialize({
      ...roomSelectorContext,
      ...filterSelection,
      dateFrom: departureDate,
      dateTo: departureDate,
      duration,
      room: roomIds,
      roomContractId: roomContractIds,
    });

    const path = apiEndpoints[transportType];

    return `${env.NEXT_PUBLIC_API_BASE_URL}/${path}?${serializedCriteria}`;
  }
  return { buildUrl };
};
